import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import SetupWizard from "@/pages/admin/twofa/SetupWizard";
import RecoveryCodesPanel from "@/pages/admin/twofa/RecoveryCodesPanel";
import DisableForm from "@/pages/admin/twofa/DisableForm";

/**
 * 2FA orchestrator — owns state + API calls. Heavy UI bits (QR wizard,
 * recovery-codes display, disable form) live in `./twofa/*` to keep this
 * file focused on flow control.
 */
const TwoFactorSection = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();

  const [status, setStatus] = useState({ enabled: false, pending: false });
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri, qr_png_b64 }
  const [setupSubmitting, setSetupSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableSubmitting, setDisableSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const ax = authedAxios();
      const { data } = await ax.get("/auth/2fa/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    queueMicrotask(() => refresh());
  }, [refresh]);

  const startSetup = async () => {
    setRecoveryCodes(null);
    try {
      const ax = authedAxios();
      const { data } = await ax.post("/auth/2fa/setup", {});
      setSetup(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const verifySetup = async (code) => {
    if (!setup || !code) return;
    setSetupSubmitting(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.post("/auth/2fa/verify", { code });
      setRecoveryCodes(data.recovery_codes);
      setSetup(null);
      await refresh();
      toast.success(
        lang === "fr"
          ? "2FA activé — conservez vos codes de secours !"
          : "2FA enabled — save your recovery codes!",
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSetupSubmitting(false);
    }
  };

  const submitDisable = async ({ password, code }, onSuccess) => {
    setDisableSubmitting(true);
    try {
      const ax = authedAxios();
      await ax.post("/auth/2fa/disable", { password, code });
      onSuccess?.();
      await refresh();
      toast.success(lang === "fr" ? "2FA désactivé" : "2FA disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setDisableSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#A19E9C] py-12">
        <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.3em]">
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </span>
      </div>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="two-factor-section"
      className="border border-[#F2EBE5]/10 p-6 md:p-10 bg-[#0a0a0a] space-y-8"
    >
      <div className="flex items-center gap-3">
        {status.enabled ? (
          <ShieldCheck className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
        ) : (
          <ShieldOff className="w-5 h-5 text-[#A19E9C]" strokeWidth={1.5} />
        )}
        <h3 className="font-serif-display text-2xl">
          {lang === "fr" ? "Authentification à 2 facteurs" : "Two-factor authentication"}
        </h3>
        <span
          data-testid="2fa-status"
          className={`ml-auto text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 border ${
            status.enabled
              ? "text-[#C5A059] border-[#C5A059]/40"
              : "text-[#A19E9C] border-[#F2EBE5]/15"
          }`}
        >
          {status.enabled
            ? lang === "fr" ? "Activé" : "Active"
            : lang === "fr" ? "Désactivé" : "Disabled"}
        </span>
      </div>

      {/* DISABLED -> enable flow CTA */}
      {!status.enabled && !setup && !recoveryCodes && (
        <div className="space-y-6">
          <p className="text-sm text-[#A19E9C] leading-relaxed">
            {lang === "fr"
              ? "Ajoutez un deuxième facteur de sécurité avec Microsoft Authenticator (ou Google Authenticator, Authy, 1Password). À chaque connexion, vous devrez saisir un code à 6 chiffres."
              : "Add a second security factor with Microsoft Authenticator (or Google Authenticator, Authy, 1Password). You'll enter a 6-digit code on every login."}
          </p>
          <button
            type="button"
            onClick={startSetup}
            data-testid="2fa-start-setup"
            className="kw-btn-primary"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            {lang === "fr" ? "Activer le 2FA" : "Enable 2FA"}
          </button>
        </div>
      )}

      {setup && (
        <SetupWizard
          setup={setup}
          lang={lang}
          submitting={setupSubmitting}
          onVerify={verifySetup}
          onCancel={() => setSetup(null)}
        />
      )}

      {recoveryCodes && (
        <RecoveryCodesPanel
          recoveryCodes={recoveryCodes}
          lang={lang}
          onDone={() => setRecoveryCodes(null)}
        />
      )}

      {status.enabled && !recoveryCodes && (
        <DisableForm
          lang={lang}
          submitting={disableSubmitting}
          onSubmit={submitDisable}
        />
      )}
    </motion.section>
  );
};

export default TwoFactorSection;
